import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import CollapsibleSection from "../CollapsibleSection";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import {
  ArrowLeft, LayoutGrid, Table2, Zap, Sparkles, GitMerge, Loader2,
  ChevronLeft, ChevronRight, CheckCircle2, XCircle, CircleDashed, FileWarning,
} from "lucide-react";

const HEALTH_META = {
  healthy: { label: "Healthy", cls: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10", color: "#10b981" },
  failing: { label: "Failing", cls: "text-red-600 border-red-500/40 bg-red-500/10", color: "#ef4444" },
  no_evidence: { label: "No evidence", cls: "text-amber-600 border-amber-500/40 bg-amber-500/10", color: "#f59e0b" },
};

function Donut({ label, pct, sublabel, color = "#5f8eed" }) {
  const data = [{ name: "done", value: pct }, { name: "rest", value: Math.max(0, 100 - pct) }];
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4" data-testid={`audit-donut-${label.toLowerCase()}`}>
      <div className="w-24 h-24 relative shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} innerRadius={34} outerRadius={46} dataKey="value" stroke="none" startAngle={90} endAngle={-270}>
              <Cell fill={color} />
              <Cell fill="hsl(var(--muted))" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center text-[15px] font-bold">{pct}%</div>
      </div>
      <div>
        <div className="text-[13px] font-semibold">{label}</div>
        <div className="text-[11.5px] text-muted-foreground mt-0.5">{sublabel}</div>
      </div>
    </div>
  );
}

function MappingDialog({ control, frameworks, onClose, onSaved }) {
  const { toast } = useToast();
  const [selected, setSelected] = useState(control?.frameworks || []);
  const [saving, setSaving] = useState(false);
  if (!control) return null;

  const toggle = (key) => setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/compliance/controls/${control.id}/mapping`, { frameworks: selected });
      toast({ title: "Mapping applied" });
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't apply mapping", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-sm" data-testid="audit-mapping-dialog">
        <DialogHeader><DialogTitle>Map {control.code} to frameworks</DialogTitle></DialogHeader>
        <p className="text-[12px] text-muted-foreground">
          This control already satisfies <strong>{control.title}</strong>. Apply it to additional
          frameworks so it counts toward their readiness too.
        </p>
        <div className="space-y-2 py-1">
          {frameworks.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`audit-mapping-checkbox-${f.key}`}>
              <Checkbox checked={selected.includes(f.key)} onCheckedChange={() => toggle(f.key)} />
              {f.name}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="audit-mapping-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="audit-mapping-save-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Apply mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AiSuggestBlock({ control }) {
  const { toast } = useToast();
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/compliance/controls/${control.id}/ai-suggest`);
      setSuggestion(data.suggestion);
    } catch (e) {
      toast({ title: "AI suggestion failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={run}
        disabled={loading}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
        data-testid={`audit-ai-suggest-btn-${control.id}`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        {loading ? "Thinking…" : "AI suggest"}
      </button>
      {suggestion && (
        <div className="mt-1.5 rounded-lg border border-primary/20 bg-primary/[0.04] p-2.5 text-[11.5px] whitespace-pre-wrap" data-testid={`audit-ai-suggestion-${control.id}`}>
          {suggestion}
        </div>
      )}
    </div>
  );
}

function ControlRow({ control, isAdmin, onMap }) {
  const meta = HEALTH_META[control.health] || HEALTH_META.no_evidence;
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`audit-control-row-${control.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[12.5px] font-medium">
            <span className="font-mono text-muted-foreground mr-1.5">{control.code}</span>{control.title}
          </div>
          {control.guidance && <div className="text-[11.5px] text-muted-foreground mt-0.5">{control.guidance}</div>}
        </div>
        <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5 shrink-0", meta.cls)}>{meta.label}</span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          <span>{control.owner || "Unassigned"}</span>
          <span>{control.evidence} evidence</span>
        </div>
        <div className="flex items-center gap-3">
          <AiSuggestBlock control={control} />
          {isAdmin && (
            <button
              type="button"
              onClick={() => onMap(control)}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
              data-testid={`audit-map-btn-${control.id}`}
            >
              <GitMerge className="w-3 h-3" /> Map
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FlashModeModal({ groups, onClose, onStatusChange }) {
  const allControls = useMemo(() => groups.flatMap((g) => g.controls), [groups]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const control = allControls[idx];
  if (!control) return null;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await onStatusChange(control, status);
      setIdx((i) => Math.min(allControls.length - 1, i + 1));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="audit-flash-mode-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /> Flash mode — {idx + 1} / {allControls.length}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-1">
          <div className="text-[12px] font-mono text-muted-foreground">{control.code}</div>
          <div className="text-[14px] font-semibold">{control.title}</div>
          {control.guidance && <p className="text-[12.5px] text-muted-foreground">{control.guidance}</p>}
        </div>
        <div className="grid grid-cols-3 gap-2 py-1">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus("implemented")} className="gap-1.5 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10" data-testid="audit-flash-implemented-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Implemented
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus("failing")} className="gap-1.5 text-red-600 border-red-500/40 hover:bg-red-500/10" data-testid="audit-flash-failing-btn">
            <XCircle className="w-3.5 h-3.5" /> Failing
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus("not_started")} className="gap-1.5 text-amber-600 border-amber-500/40 hover:bg-amber-500/10" data-testid="audit-flash-not-started-btn">
            <CircleDashed className="w-3.5 h-3.5" /> No evidence
          </Button>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div className="flex gap-1.5">
            <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="audit-flash-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" disabled={idx === allControls.length - 1} onClick={() => setIdx((i) => Math.min(allControls.length - 1, i + 1))} data-testid="audit-flash-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="audit-flash-done-btn">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AuditReadiness({ auditId, isAdmin, frameworks, onBack, onOpenFindings }) {
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState("groups");
  const [flashOpen, setFlashOpen] = useState(false);
  const [mappingControl, setMappingControl] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/compliance/audits/${auditId}/readiness`);
    setData(d);
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (control, status) => {
    await api.put(`/compliance/controls/${control.id}`, { status });
    await load();
  };

  if (!data) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  const allControlsFlat = data.groups.flatMap((g) => g.controls.map((c) => ({ ...c, section: g.label })));
  const filteredFlat = search.trim()
    ? allControlsFlat.filter((c) => `${c.code} ${c.title}`.toLowerCase().includes(search.toLowerCase()))
    : allControlsFlat;

  return (
    <div className="space-y-4" data-testid="audit-readiness-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground" data-testid="audit-readiness-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to audits
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onOpenFindings?.()} data-testid="audit-readiness-findings-btn">
            <FileWarning className="w-3.5 h-3.5" /> Findings ({data.findings_open})
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setFlashOpen(true)} data-testid="audit-flash-mode-btn">
              <Zap className="w-3.5 h-3.5" /> Flash mode
            </Button>
          )}
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            <button type="button" onClick={() => setViewMode("groups")} className={cn("px-2.5 h-8 flex items-center gap-1.5 text-[12px]", viewMode === "groups" ? "bg-primary text-primary-foreground" : "hover:bg-muted")} data-testid="audit-view-groups-btn">
              <LayoutGrid className="w-3.5 h-3.5" /> Groups
            </button>
            <button type="button" onClick={() => setViewMode("table")} className={cn("px-2.5 h-8 flex items-center gap-1.5 text-[12px]", viewMode === "table" ? "bg-primary text-primary-foreground" : "hover:bg-muted")} data-testid="audit-view-table-btn">
              <Table2 className="w-3.5 h-3.5" /> Table
            </button>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[16px] font-semibold">{data.audit_name}</div>
        <div className="text-[12px] text-muted-foreground">{data.framework_name} · {data.stage} · {data.period_start} – {data.period_end}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Donut label="Compliance" pct={data.maturity_pct} sublabel={`${data.controls_healthy} / ${data.controls_total} controls healthy`} color="#10b981" />
        <Donut label="Progress" pct={data.progress_pct} sublabel={`Stage: ${data.stage}`} color="#5f8eed" />
      </div>

      {viewMode === "groups" ? (
        <div className="space-y-2.5">
          {data.groups.map((g) => (
            <CollapsibleSection
              key={g.label}
              id={`audit-${auditId}-${g.label}`}
              storagePrefix="ucv:audit-readiness:collapsed:"
              defaultOpen
              title={g.label}
              description={`${g.healthy} / ${g.total} healthy`}
              rightSlot={
                <div className="w-28 h-1.5 rounded-full bg-muted overflow-hidden" data-testid={`audit-group-progress-${g.label}`}>
                  <div className="h-full bg-primary rounded-full" style={{ width: `${g.pct}%` }} />
                </div>
              }
            >
              <div className="space-y-2">
                {g.controls.map((c) => (
                  <ControlRow key={c.id} control={c} isAdmin={isAdmin} onMap={setMappingControl} />
                ))}
              </div>
            </CollapsibleSection>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <Input placeholder="Search controls…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="audit-table-search-input" />
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table data-testid="audit-controls-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead><TableHead>Title</TableHead>
                  <TableHead className="hidden sm:table-cell">Section</TableHead>
                  <TableHead>Health</TableHead><TableHead>Owner</TableHead>
                  <TableHead className="hidden sm:table-cell">Evidence</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFlat.map((c) => {
                  const meta = HEALTH_META[c.health] || HEALTH_META.no_evidence;
                  return (
                    <TableRow key={c.id} data-testid={`audit-control-row-${c.id}`}>
                      <TableCell className="font-mono text-[12px]">{c.code}</TableCell>
                      <TableCell className="text-[12.5px]">{c.title}</TableCell>
                      <TableCell className="hidden sm:table-cell text-[11.5px] text-muted-foreground">{c.section}</TableCell>
                      <TableCell><span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", meta.cls)}>{meta.label}</span></TableCell>
                      <TableCell className="text-[12px]">{c.owner || "—"}</TableCell>
                      <TableCell className="hidden sm:table-cell text-[12px]">{c.evidence}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3 flex-wrap">
                          <AiSuggestBlock control={c} />
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => setMappingControl(c)}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                              data-testid={`audit-map-btn-${c.id}`}
                            >
                              <GitMerge className="w-3 h-3" /> Map
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {flashOpen && (
        <FlashModeModal groups={data.groups} onClose={() => { setFlashOpen(false); load(); }} onStatusChange={handleStatusChange} />
      )}
      {mappingControl && (
        <MappingDialog
          control={mappingControl}
          frameworks={frameworks}
          onClose={() => setMappingControl(null)}
          onSaved={() => { setMappingControl(null); load(); }}
        />
      )}
    </div>
  );
}
